import React from 'react';

interface timchehLoaderProps {
  size?: 'small' | 'medium' | 'large';
  fullScreen?: boolean;
  message?: string;
  className?: string;
}

const TimchehLoader: React.FC<timchehLoaderProps> = ({ 
  size = 'medium', 
  fullScreen = false, 
  message = 'در حال بارگذاری...',
  className = ''
}) => {
  const sizeClasses = {
    small: 'w-16 h-16',
    medium: 'w-24 h-24',
    large: 'w-32 h-32'
  };

  const textSizeClasses = {
    small: 'text-lg',
    medium: 'text-xl',
    large: 'text-2xl'
  };

  const containerClasses = fullScreen 
    ? 'fixed inset-0 z-50 flex items-center justify-center'
    : `flex items-center justify-center ${className}`;

  return (
    <div className={containerClasses} dir="rtl">
      {/* Dim Overlay Background for fullScreen mode */}
      {fullScreen && (
        <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" />
      )}
      
      {/* Main Content Container */}
      <div className={`relative ${fullScreen ? 'bg-gradient-to-br from-blue-50 via-white to-red-50 rounded-3xl p-8 shadow-2xl border border-white/20' : ''}`}>
      {/* Floating Background Particles */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {[...Array(20)].map((_, i) => (
          <div
            key={i}
            className="absolute rounded-full opacity-20"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              width: `${Math.random() * 4 + 2}px`,
              height: `${Math.random() * 4 + 2}px`,
              backgroundColor: i % 2 === 0 ? '#3B82F6' : '#EF4444',
              animation: `float ${Math.random() * 3 + 2}s ease-in-out infinite alternate`,
              animationDelay: `${Math.random() * 2}s`
            }}
          />
        ))}
      </div>

      <div className="relative flex flex-col items-center space-y-8">
        {/* Main Spectacular Loader */}
        <div className="relative flex items-center justify-center">
          {/* Outer Morphing Ring */}
          <div className={`${sizeClasses[size]} relative`}>
            <div className="absolute inset-0 rounded-full bg-gradient-to-r from-blue-500 via-purple-500 to-red-500 animate-spin opacity-80"
                 style={{ 
                   clipPath: 'polygon(0% 0%, 100% 0%, 80% 100%, 20% 100%)',
                   animation: 'morph-spin 3s linear infinite'
                 }}>
            </div>
            <div className="absolute inset-1 rounded-full bg-gradient-to-l from-red-400 via-pink-400 to-blue-400 animate-spin opacity-70"
                 style={{ 
                   animationDirection: 'reverse',
                   animationDuration: '2s'
                 }}>
            </div>
            <div className="absolute inset-2 rounded-full bg-gradient-to-r from-cyan-300 to-violet-300 animate-pulse">
            </div>
          </div>

          {/* Middle Breathing Ring */}
          <div className={`absolute ${sizeClasses[size]} animate-pulse`}
               style={{ animation: 'breathe 2s ease-in-out infinite alternate' }}>
            <div className="absolute inset-3 rounded-full border-4 border-transparent bg-gradient-to-r from-blue-600 to-red-600 bg-clip-border"
                 style={{
                   background: 'linear-gradient(45deg, transparent 30%, rgba(59, 130, 246, 0.3) 50%, transparent 70%), linear-gradient(-45deg, transparent 30%, rgba(239, 68, 68, 0.3) 50%, transparent 70%)',
                   animation: 'rainbow-border 2s linear infinite'
                 }}>
            </div>
          </div>

          {/* Inner Dynamic Core */}
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="relative">
              {/* Pulsing Core */}
              <div className="w-8 h-8 rounded-full bg-gradient-to-r from-blue-600 via-purple-600 to-red-600 animate-ping opacity-75"></div>
              <div className="absolute inset-0 w-8 h-8 rounded-full bg-gradient-to-l from-red-500 via-pink-500 to-blue-500 animate-pulse"></div>
              
              {/* Rotating Diamonds */}
              <div className="absolute -inset-2">
                {[...Array(4)].map((_, i) => (
                  <div
                    key={i}
                    className="absolute w-2 h-2 bg-gradient-to-r from-yellow-400 to-orange-500 transform rotate-45"
                    style={{
                      top: '50%',
                      left: '50%',
                      transformOrigin: '50% 50%',
                      animation: `orbit ${2 + i * 0.5}s linear infinite`,
                      animationDelay: `${i * 0.25}s`
                    }}
                  />
                ))}
              </div>
            </div>
          </div>

          {/* Floating Energy Orbs */}
          <div className="absolute inset-0">
            {[...Array(6)].map((_, i) => (
              <div
                key={i}
                className="absolute w-3 h-3 rounded-full bg-gradient-to-r from-cyan-400 to-blue-600 opacity-60"
                style={{
                  animation: `float-orbit ${3 + i * 0.3}s ease-in-out infinite`,
                  animationDelay: `${i * 0.5}s`,
                  filter: 'blur(0.5px) drop-shadow(0 0 8px rgba(59, 130, 246, 0.6))'
                }}
              />
            ))}
          </div>
        </div>

        {/* Enhanced timcheh Branding */}
        <div className="text-center relative">
          {/* Glowing Background */}
          <div className="absolute inset-0 bg-gradient-to-r from-blue-600 to-red-600 rounded-2xl opacity-10 blur-xl animate-pulse"></div>
          
          <div className="relative bg-white/80 backdrop-blur-sm rounded-2xl px-8 py-4 border border-white/20 shadow-2xl">
            <h3 className={`font-semibold tracking-wider mb-2 ${textSizeClasses[size]}`}
                style={{
                  background: 'linear-gradient(45deg, #3B82F6, #8B5CF6, #EF4444, #F59E0B)',
                  backgroundSize: '300% 300%',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  animation: 'gradient-shift 3s ease infinite'
                }}>
              ✨ timcheh ✨
            </h3>
            <p className={`text-gray-700 font-medium ${textSizeClasses[size === 'large' ? 'medium' : 'small']} animate-pulse`}>
              {message}
            </p>
          </div>
        </div>

        {/* Advanced Loading Dots with Morphing Effect */}
        <div className="flex space-x-2 space-x-reverse">
          {[...Array(5)].map((_, i) => (
            <div
              key={i}
              className="relative"
              style={{ animationDelay: `${i * 0.2}s` }}
            >
              <div 
                className="w-3 h-3 rounded-full animate-bounce"
                style={{
                  background: `linear-gradient(45deg, ${i % 2 === 0 ? '#3B82F6' : '#EF4444'}, ${i % 2 === 0 ? '#8B5CF6' : '#F59E0B'})`,
                  boxShadow: `0 0 20px ${i % 2 === 0 ? 'rgba(59, 130, 246, 0.5)' : 'rgba(239, 68, 68, 0.5)'}`,
                  animationDelay: `${i * 0.15}s`,
                  animation: `morph-bounce 1.5s ease-in-out infinite`
                }}
              />
              {/* Trailing effect */}
              <div 
                className="absolute inset-0 w-3 h-3 rounded-full opacity-30 animate-ping"
                style={{
                  background: `linear-gradient(45deg, ${i % 2 === 0 ? '#3B82F6' : '#EF4444'}, ${i % 2 === 0 ? '#8B5CF6' : '#F59E0B'})`,
                  animationDelay: `${i * 0.15}s`
                }}
              />
            </div>
          ))}
        </div>

        {/* Percentage or Progress Indication */}
        <div className="w-48 h-1 bg-gray-200 rounded-full overflow-hidden">
          <div className="h-full bg-gradient-to-r from-blue-500 via-purple-500 to-red-500 rounded-full animate-pulse"
               style={{
                 width: '60%',
                 animation: 'progress-wave 2s ease-in-out infinite'
               }}>
          </div>
        </div>
      </div>
      </div>


    </div>
  );
};

export default TimchehLoader; 